import { DATA, Data } from '@wix/component-protocol/schema';
import { DataTypes, Link, Rel } from '@wix/document-services-types';
import type {
  ElementLink,
  ElementData,
  ElementDataInput,
  ElementDataValue,
  ElementMenuItems,
  ElementRichText,
  ElementImage,
  ElementVideo,
  ElementVectorArt,
  PublicDmTypes,
} from '@wix/public-editor-platform-interfaces';
import { PlatformPrivateServices } from '../../PlatformPrivateServices';

// Local type matching ElementMenuItem from interfaces (avoids build order dependency)
type ElementMenuItem = Omit<PublicDmTypes.MenuItem, 'link' | 'items'> & {
  link?: ElementLink;
  items?: ElementMenuItem[];
};

export type { ElementLink, ElementData, ElementDataInput, ElementDataValue };

export class PublicDataAdapter {
  constructor(private ps: PlatformPrivateServices) {}

  #itemToPublicData(
    value: unknown,
    definition: Data[string],
  ): ElementDataValue {
    switch (definition?.dataType) {
      case DATA.DATA_TYPE.link:
        return this.toPublicLink(value as Link);

      case DATA.DATA_TYPE.menuItems:
        return this.toPublicMenuItems(value as DataTypes.MenuItems);

      case DATA.DATA_TYPE.richText:
        return this.toPublicRichText(value as DataTypes.RichText);

      case DATA.DATA_TYPE.image:
        return this.toPublicImage(value as DataTypes.Image);

      case DATA.DATA_TYPE.video:
        return this.toPublicVideo(value as DataTypes.Video);

      case DATA.DATA_TYPE.vectorArt:
        return this.toPublicVectorArt(value as DataTypes.VectorArt);

      case DATA.DATA_TYPE.arrayItems: {
        const dataDefinitions = definition.arrayItems?.data?.items;
        if (dataDefinitions) {
          return ((value || []) as Data[]).map((itemData) => {
            return this.toPublicData(itemData, dataDefinitions);
          });
        }

        const dataDefinition = definition.arrayItems?.dataItem;
        if (dataDefinition) {
          return this.#itemToPublicData(value, dataDefinition);
        }
        return value as ElementDataValue;
      }

      default:
        return value as ElementDataValue;
    }
  }

  public toPublicData(
    data: Record<string, unknown>,
    definitions: Data,
  ): ElementData {
    return Object.entries(data).reduce((acc, [key, value]) => {
      const definition = definitions[key];

      acc[key] = this.#itemToPublicData(value, definition);

      return acc;
    }, {} as ElementData);
  }

  #itemFromPublicData(value: unknown, definition: Data[string]): unknown {
    switch (definition?.dataType) {
      case DATA.DATA_TYPE.link:
        return this.fromPublicLink(value as ElementLink);

      case DATA.DATA_TYPE.menuItems:
        return this.fromPublicMenuItems(value as ElementMenuItems);

      case DATA.DATA_TYPE.richText:
        return this.fromPublicRichText(value as ElementRichText);

      case DATA.DATA_TYPE.image:
        return this.fromPublicImage(value as ElementImage);

      case DATA.DATA_TYPE.video:
        return this.fromPublicVideo(value as ElementVideo);

      case DATA.DATA_TYPE.vectorArt:
        return this.fromPublicVectorArt(value as ElementVectorArt);

      case DATA.DATA_TYPE.arrayItems: {
        const dataDefinitions = definition.arrayItems?.data?.items;
        if (dataDefinitions) {
          return (value as ElementDataInput[]).map((itemData) => {
            return this.fromPublicData(itemData, dataDefinitions);
          });
        }

        // if single item
        const dataDefinition = definition.arrayItems?.dataItem;
        if (dataDefinition) {
          return this.#itemFromPublicData(value, dataDefinition);
        }
        return value;
      }

      default:
        return value;
    }
  }

  public fromPublicData(
    data: ElementDataInput,
    definitions: Data,
  ): Record<string, unknown> {
    return Object.entries(data).reduce((acc, [key, value]) => {
      const definition = definitions[key];

      acc[key] = this.#itemFromPublicData(value, definition);

      return acc;
    }, {} as Record<string, unknown>);
  }

  public toPublicLink(link?: Link): ElementLink | undefined {
    if (!link) {
      return undefined;
    }
    return {
      ...link,
      displayName:
        this.ps.platformPackagesBridge.LinkServiceAPI?.getDisplayName(link) ??
        '',
    } as ElementLink;
  }

  // at this point we're manually removing properties that came from viewer.
  // later on we will have a proper mapping function that doesn't rely on viewer.
  public fromPublicLink(link?: ElementLink): Link | undefined {
    if (!link) {
      return undefined;
    }

    const dmLink: Link = { ...link };

    const safeRemoveProperty = (propName: string) => {
      if (propName in dmLink) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete dmLink[propName as keyof Link];
      }
    };

    safeRemoveProperty('displayName');
    safeRemoveProperty('href');

    if (!['PageLink', 'ExternalLink'].includes(dmLink.type)) {
      safeRemoveProperty('target');
    }

    if ('rel' in link && 'rel' in dmLink && typeof link.rel === 'string') {
      dmLink.rel = (link.rel as string).split(' ') as Rel;
    }

    return dmLink;
  }

  #mapMenuItemToPublic(item: DataTypes.MenuItem): ElementMenuItem {
    const { type: _type, isVisible, ...rest } = item;
    return {
      ...rest,
      visible: isVisible,
      link: item.link
        ? this.toPublicLink(item.link as unknown as Link)
        : undefined,
      items: item.items?.map((subItem: DataTypes.MenuItem) =>
        this.#mapMenuItemToPublic(subItem),
      ),
    } as ElementMenuItem;
  }

  #mapMenuItemFromPublic(item: ElementMenuItem): DataTypes.MenuItem {
    const mappedItems = item.items?.map((subItem: ElementMenuItem) =>
      this.#mapMenuItemFromPublic(subItem),
    );
    return {
      type: 'Builder.MenuItem',
      container: item.container ?? undefined,
      label: item.label ?? undefined,
      isVisible: item.visible ?? undefined,
      link: item.link
        ? (this.fromPublicLink(item.link) as unknown as DataTypes.Link)
        : undefined,
      items: mappedItems,
    };
  }

  public toPublicMenuItems(
    menuItems?: DataTypes.MenuItems,
  ): ElementMenuItems | undefined {
    if (!menuItems) {
      return undefined;
    }
    const mappedItems = menuItems.items?.map((item: DataTypes.MenuItem) =>
      this.#mapMenuItemToPublic(item),
    );
    // Internal type doesn't have appDefinitionId, but it may exist at runtime
    const { name, appDefinitionId } = menuItems as DataTypes.MenuItems & {
      appDefinitionId?: string;
    };
    return {
      name,
      appDefinitionId,
      items: mappedItems,
    } as ElementMenuItems;
  }

  public fromPublicMenuItems(
    menuItems?: ElementMenuItems,
  ): DataTypes.MenuItems | undefined {
    if (!menuItems) {
      return undefined;
    }
    // Cast items to work around type differences between ElementLink and PublicDmTypes.Link
    const items = menuItems.items as unknown as ElementMenuItem[] | undefined;
    const mappedItems = items?.map((item: ElementMenuItem) =>
      this.#mapMenuItemFromPublic(item),
    );
    // Internal type doesn't have appDefinitionId, but we pass it through for compatibility
    const { appDefinitionId } = menuItems as ElementMenuItems & {
      appDefinitionId?: string;
    };
    return {
      type: 'Builder.MenuItems',
      name: menuItems.name ?? undefined,
      appDefinitionId,
      items: mappedItems,
    } as DataTypes.MenuItems;
  }

  public toPublicRichText(
    richText?: DataTypes.RichText,
  ): ElementRichText | undefined {
    if (!richText) {
      return undefined;
    }
    // Internal linkList is string[] (link references), but runtime may have Link objects
    // due to how data is resolved. Handle both cases for backwards compatibility.
    const mappedLinkList = richText.linkList
      ?.map((link) =>
        typeof link === 'string'
          ? undefined
          : this.toPublicLink(link as unknown as Link),
      )
      .filter((link): link is ElementLink => link !== undefined);
    return {
      text: richText.text,
      linkList: mappedLinkList,
    } as ElementRichText;
  }

  public fromPublicRichText(
    richText?: ElementRichText,
  ): DataTypes.RichText | undefined {
    if (!richText) {
      return undefined;
    }
    // The internal type declares linkList as string[], but runtime actually uses Link objects.
    // We convert ElementLink[] to Link[] and cast to satisfy the incorrect type definition.
    const mappedLinkList = richText.linkList
      ?.map((link) => this.fromPublicLink(link))
      .filter((link): link is Link => link !== undefined);
    return {
      type: 'Builder.RichText',
      text: richText.text,
      linkList: mappedLinkList as unknown as string[],
    };
  }

  public toPublicImage(image?: DataTypes.Image): ElementImage | undefined {
    if (!image) {
      return undefined;
    }
    const { type: _type, ...rest } = image;
    return rest as ElementImage;
  }

  public fromPublicImage(image?: ElementImage): DataTypes.Image | undefined {
    if (!image) {
      return undefined;
    }
    return {
      type: 'Builder.Image',
      ...image,
    } as DataTypes.Image;
  }

  public toPublicVideo(video?: DataTypes.Video): ElementVideo | undefined {
    if (!video) {
      return undefined;
    }
    const { type: _type, poster, ...rest } = video;
    return {
      ...rest,
      // Internal poster is Image object, public is string (URI)
      // poster: poster?.uri ?? null,
      poster,
    };
  }

  public fromPublicVideo(video?: ElementVideo): DataTypes.Video | undefined {
    if (!video) {
      return undefined;
    }
    const { poster, ...rest } = video;
    return {
      type: 'Builder.Video',
      ...rest,
      // Public poster is string (URI), internal is Image object
      // poster: poster ? { type: 'Builder.Image', uri: poster } : undefined,
      poster: poster ? { type: 'Builder.Image', ...poster } : undefined,
    };
  }

  public toPublicVectorArt(
    vectorArt?: DataTypes.VectorArt,
  ): ElementVectorArt | undefined {
    if (!vectorArt) {
      return undefined;
    }
    const { type: _type, ...rest } = vectorArt;
    return rest as ElementVectorArt;
  }

  public fromPublicVectorArt(
    vectorArt?: ElementVectorArt,
  ): DataTypes.VectorArt | undefined {
    if (!vectorArt) {
      return undefined;
    }
    return {
      type: 'Builder.VectorArt',
      ...vectorArt,
    } as DataTypes.VectorArt;
  }
}
